import React, { useEffect, useRef, useState } from 'react';
import styles from './RowActionsMenu.module.css';

interface RowActionsMenuProps {
  onEdit?: () => void;
  onDelete?: () => void;
  editLabel?: string;
  deleteLabel?: string;
  /**
   * Optional extra menu items rendered after Edit / Delete.
   * Each item is { label, onClick, destructive? } — destructive renders red.
   */
  extra?: { label: string; onClick: () => void; destructive?: boolean }[];
}

export const RowActionsMenu: React.FC<RowActionsMenuProps> = ({
  onEdit,
  onDelete,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  extra,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handle = (fn?: () => void) => () => {
    setOpen(false);
    fn?.();
  };

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className={styles.menu} role="menu">
          {onEdit && (
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={handle(onEdit)}
            >
              {editLabel}
            </button>
          )}
          {extra?.map((it, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={`${styles.item} ${it.destructive ? styles.itemDestructive : ''}`}
              onClick={handle(it.onClick)}
            >
              {it.label}
            </button>
          ))}
          {onDelete && (
            <button
              type="button"
              role="menuitem"
              className={`${styles.item} ${styles.itemDestructive}`}
              onClick={handle(onDelete)}
            >
              {deleteLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RowActionsMenu;
